// Spec helpers for Alerts widget
// We intentionally do NOT modify or import the existing strategy builder modules.
// For advanced mode we will reuse buildGraphFromSpec via a dynamic import to avoid coupling.

// Flattened simple rule type:
// {
//   timeframe: 'M1'|'M5'|..., // base timeframe
//   rules: Array<{
//     lhs: Operand, // price or indicator
//     op: 'crosses_above'|'crosses_below'|'greater_than'|'less_than'|'equals'|'not_equals',
//     rhs: Operand  // number | price | indicator
//   }>
// }
// Operand shapes:
//  - number
//  - { type:'price', source:'close'|'open'|'high'|'low', timeframe? }
//  - { type:'indicator', name:string, params?:object, input?: { type:'price'| 'indicator', ...}, timeframe?, output? }

const isNumber = (v) => typeof v === 'number' && Number.isFinite(v);

function normalizeIndicatorOperand(op) {
  if (!op) return op;
  const name = String(op.name || '').toLowerCase();
  const params = op.params ? Object.fromEntries(Object.entries(op.params).sort(([a],[b]) => a.localeCompare(b))) : {};
  const out = { type: 'indicator', name, params };
  if (op.timeframe) out.timeframe = op.timeframe;
  if (op.output) out.output = op.output;
  if (op.input) {
    if (op.input.type === 'price') {
      out.inputs = [{ ref: 'PRICE', params: { field: op.input.source || 'close' } }];
    } else if (op.input.type === 'indicator') {
      out.inputs = [normalizeIndicatorOperand(op.input)];
    }
  }
  return out;
}

function toBackendOperand(op) {
  if (op == null) return 0;
  if (isNumber(op)) return op;
  if (typeof op !== 'object') return op;
  if (op.type === 'price') {
    return { type: 'price', source: op.source || 'close', timeframe: op.timeframe };
  }
  if (op.type === 'indicator' || op.name) {
    return normalizeIndicatorOperand(op);
  }
  // Already backend-ish
  return op;
}

// --- Minimal node graph construction for simple mode ---
function stableHash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = (h * 33) ^ str.charCodeAt(i);
  return (h >>> 0).toString(36);
}
function normalizeParams(params) {
  const keys = Object.keys(params || {}).sort();
  const out = {}; keys.forEach((k) => (out[k] = params[k]));
  return out;
}
function summarizeInput(inp) {
  if (!inp) return 'price:close';
  if (inp.type === 'price') return { t: 'price', s: inp.source || 'close', tf: inp.timeframe || undefined };
  if (inp.type === 'indicator') return { n: inp.name, p: normalizeParams(inp.params || {}), i: summarizeInput(inp.input), tf: inp.timeframe || undefined };
  return 'price:close';
}
function stableNodeId(ind) {
  const payload = {
    n: String(ind?.name || '').toUpperCase(),
    p: normalizeParams(ind?.params || {}),
    i: ind?.input ? summarizeInput(ind.input) : 'price:close',
    tf: ind?.timeframe || undefined,
  };
  return `${payload.n}_${stableHash(JSON.stringify(payload))}`.toLowerCase();
}
function ensurePriceNode(nodes, source = 'close', timeframe) {
  const id = `price_${String(source).toLowerCase()}${timeframe ? '_' + String(timeframe).toLowerCase() : ''}`;
  if (!nodes.some((n) => n.id === id)) nodes.push({ id, name: 'PRICE', params: { source }, inputs: undefined, timeframe });
  return id;
}
function ensureIndicatorNode(nodes, ind) {
  const id = stableNodeId(ind);
  if (nodes.some((n) => n.id === id)) return id;
  let sourceId;
  const input = ind.input;
  if (!input) sourceId = ensurePriceNode(nodes, 'close', ind?.timeframe);
  else if (input.type === 'price') sourceId = ensurePriceNode(nodes, input.source || 'close', input.timeframe || ind?.timeframe);
  else if (input.type === 'indicator') sourceId = ensureIndicatorNode(nodes, input);
  else sourceId = ensurePriceNode(nodes, 'close', ind?.timeframe);
  nodes.push({ id, name: String(ind.name || '').toUpperCase(), params: normalizeParams(ind.params || {}), inputs: { source: sourceId }, timeframe: ind?.timeframe });
  return id;
}

export function buildSimpleSectionedSpec(simple) {
  const timeframe = simple?.timeframe || 'M1';
  const nodes = [];
  const visit = (op) => {
    if (!op || typeof op !== 'object') return;
    if (op.type === 'price') { ensurePriceNode(nodes, op.source || 'close', op.timeframe); return; }
    if (op.type === 'indicator' || op.name) {
      const ind = op.type === 'indicator' ? op : { type: 'indicator', name: op.name, params: op.params, input: op.input, timeframe: op.timeframe };
      ensureIndicatorNode(nodes, ind);
    }
  };
  const clauses = (simple?.rules || []).map((r) => {
    visit(r.lhs); visit(r.rhs);
    return { lhs: toBackendOperand(r.lhs), op: r.op, rhs: toBackendOperand(r.rhs) };
  });
  return { timeframe, nodes, entry: { op: 'AND', clauses }, risk: {} };
}

export async function buildAdvancedSectionedSpec(uiSpec, opts = {}) {
  // Dynamically import to avoid tight coupling; if module layout changes, the alerts widget remains isolated
  const mod = await import('../lib/sectioned/serialize.js');
  const { buildGraphFromSpec } = mod;
  const graph = buildGraphFromSpec(uiSpec, opts);
  // Alerts need only conditions; strip risk to keep payload minimal
  const { nodes, entry_long, entry_short, exit_long, exit_short, timeframe } = graph;
  return { nodes, entry_long, entry_short, exit_long, exit_short, timeframe, risk: {} };
}
